import React, { FC, useMemo } from 'react';
import { Box, Card, Chip, Grid, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import { getMeasurementUnit } from '../../helpers/general.helpers';
import { UserAvatar } from '../../components/user-avatar/user-avatar.component';
import { combineShoeSizeDisplay, getStandardValue, type OwnerPayload } from '../../helpers/equipment.helpers';

export type { OwnerPayload };

/** API keys / labels for lookup; second element is short grid label */
const SKI_SPEC_GRID: { apiLabel: string; shortLabel: string }[] = [
  { apiLabel: 'Grind', shortLabel: 'GRIND' },
  { apiLabel: 'Base Material', shortLabel: 'BASE' },
  { apiLabel: 'Snow Designation', shortLabel: 'SNOW' },
  { apiLabel: 'Camber Height', shortLabel: 'CAMBER' },
  { apiLabel: 'MF 0.2', shortLabel: 'MF 0.2' },
  { apiLabel: 'Ski Length in cm', shortLabel: 'LENGTH' },
];

const SHOE_SPEC_GRID: { apiLabel: string; shortLabel: string }[] = [
  { apiLabel: 'Stack Height', shortLabel: 'STACK' },
  { apiLabel: 'Drop', shortLabel: 'DROP' },
  { apiLabel: 'Cushioning', shortLabel: 'CUSHION' },
  { apiLabel: 'Terrain Type', shortLabel: 'TERRAIN' },
  { apiLabel: 'Weight', shortLabel: 'WEIGHT' },
  { apiLabel: 'Size', shortLabel: 'SIZE' },
];

export interface EquipmentManager {
  id: number;
  firstname?: string | null;
  lastname?: string | null;
  profile_image?: string | null;
  hidden: boolean;
}

export interface SearchEquipmentItem {
  id: number;
  public_id?: string | null;
  name: string;
  brand?: string | null;
  year?: number | null;
  equipment_type?: { id?: number; name?: string | null } | null;
  equipment_subtype?: { id?: number; name?: string | null } | null;
  standard_field_values?: Record<string, unknown>;
  custom_field_values?: Record<string, string>;
  published?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  is_owner?: boolean;
  totalDistanceUsed?: number | null;
  default_sport?: { id?: number; name?: string | null } | null;
  default_sport_type?: { id?: number; name?: string | null } | null;
  managers?: EquipmentManager[] | null;
}

export interface EquipmentSearchCardProps {
  item: SearchEquipmentItem;
  onOpen: (equipmentId: number, equipmentTypeId?: number) => void;
}

/** SNOW cell text color from designation wording (matches label gray for `zero`). */
function snowCellColor(display: string): 'text.primary' | 'text.secondary' | 'primary.main' | 'error.main' {
  if (display === 'N/A') return 'text.primary';
  const t = display.trim().toLowerCase();
  if (t === 'zero') return 'text.secondary';
  if (t.includes('warm')) return 'error.main';
  if (t.includes('cold')) return 'primary.main';
  return 'text.primary';
}

function getSubtypeChipSx(theme: Theme, name: string) {
  const base = {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    '& .MuiChip-label': { px: 1 },
  };
  const n = name.trim().toLowerCase();
  if (n === 'classic') {
    return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
  }
  if (n === 'skate') {
    return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.12), color: 'text.primary' };
  }
  if (n === 'other') {
    return { ...base, bgcolor: 'grey.900', color: 'common.white' };
  }
  return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
}

function getTypeChipSx(theme: Theme, typeName: string) {
  const base = {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    '& .MuiChip-label': { px: 1 },
  };
  if (typeName === 'xc skis') {
    return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
  }
  if (typeName === 'shoes') {
    return { ...base, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.main' };
  }
  if (typeName === 'other') {
    return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.12), color: 'text.primary' };
  }
  return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
}

function getShoeSubtypeChipSx(theme: Theme, name: string) {
  const base = {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    '& .MuiChip-label': { px: 1 },
  };
  const n = name.trim().toLowerCase();
  if (n === 'running') {
    return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
  }
  if (n === 'boots') {
    return { ...base, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.main' };
  }
  if (n === 'hiking') {
    return { ...base, bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.main' };
  }
  if (n === 'cleats') {
    return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.12), color: 'text.primary' };
  }
  return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.12), color: 'text.primary' };
}

type SpecCell = {
  id: string;
  shortLabel: string;
  display: string;
  valueColor: 'text.primary' | 'text.secondary' | 'primary.main' | 'error.main';
  isBlank?: boolean;
};

const EquipmentSearchCard: FC<EquipmentSearchCardProps> = ({ item, onOpen }) => {
  const theme = useTheme();
  const distanceUnit = getMeasurementUnit();
  const rawDist = item.totalDistanceUsed;
  const distNum = typeof rawDist === 'number' ? rawDist : rawDist != null ? Number(rawDist) : NaN;
  const showDistanceUsed = Number.isFinite(distNum) && distNum > 0;
  const defaultSportName = item.default_sport?.name?.trim();
  const defaultSportTypeName = item.default_sport_type?.name?.trim();
  const defaultLabel = defaultSportName
    ? defaultSportTypeName
      ? `${defaultSportName} · ${defaultSportTypeName}`
      : defaultSportName
    : null;

  const sv = item.standard_field_values || {};
  const typeName = (item.equipment_type?.name ?? '').trim().toLowerCase();
  const isSkiType = typeName === 'xc skis';
  const isShoeType = typeName === 'shoes';
  const isOtherType = typeName === 'other';

  const visibleManagers = useMemo(() => (item.managers ?? []).filter((m) => !m.hidden), [item.managers]);

  const brandYear = [item.brand?.trim(), item.year != null ? String(item.year) : ''].filter(Boolean).join(' · ') || '—';

  const specCells = useMemo<SpecCell[]>(() => {
    if (isSkiType) {
      return SKI_SPEC_GRID.map(({ apiLabel, shortLabel }) => {
        const entry = getStandardValue(sv, apiLabel);
        const val = entry?.value ?? '';
        const unit = entry?.unit ?? '';
        const displayRaw = val || 'N/A';
        const display = displayRaw === 'N/A' ? 'N/A' : unit ? `${val} ${unit}` : val;
        const valueColor = shortLabel === 'SNOW' ? snowCellColor(displayRaw) : 'text.primary';
        return { id: apiLabel, shortLabel, display, valueColor };
      });
    }

    if (isShoeType) {
      return SHOE_SPEC_GRID.map(({ apiLabel, shortLabel }) => {
        if (shortLabel === 'SIZE') {
          return { id: apiLabel, shortLabel, display: combineShoeSizeDisplay(sv), valueColor: 'text.primary' };
        }
        const entry = getStandardValue(sv, apiLabel);
        const val = entry?.value ?? '';
        const unit = entry?.unit ?? '';
        const display = val ? (unit ? `${val} ${unit}` : val) : 'N/A';
        return { id: apiLabel, shortLabel, display, valueColor: 'text.primary' };
      });
    }

    if (isOtherType) {
      return SKI_SPEC_GRID.map(({ apiLabel }) => ({
        id: apiLabel,
        shortLabel: '',
        display: '',
        valueColor: 'text.primary' as const,
        isBlank: true,
      }));
    }

    return SKI_SPEC_GRID.map(({ apiLabel, shortLabel }) => ({
      id: apiLabel,
      shortLabel,
      display: 'N/A',
      valueColor: shortLabel === 'SNOW' ? 'text.secondary' : ('text.primary' as const),
    }));
  }, [isOtherType, isShoeType, isSkiType, sv]);

  return (
    <Card
      onClick={() => onOpen(item.id, item.equipment_type?.id)}
      sx={{
        width: '100%',
        maxWidth: '100%',
        height: 250,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: defaultLabel ? `1.5px solid ${theme.palette.primary.light}` : '1px solid transparent',
        background: defaultLabel
          ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
              theme.palette.primary.main,
              0.035
            )} 28%, transparent 38%)`
          : undefined,
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
        },
      }}
    >
      {defaultLabel && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: (t) => `${t.palette.primary.main}18`,
            borderRadius: 1,
            px: 1,
            py: 0.25,
            mb: 0.75,
          }}
        >
          <Typography
            sx={{
              color: 'primary.main',
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              lineHeight: 1.4,
            }}
          >
            ★ Default for {defaultLabel}
          </Typography>
        </Box>
      )}
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            color: 'primary.main',
            fontWeight: 700,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.name}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          mt: 0.5,
          mb: 0.75,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.8125rem',
            minWidth: 0,
            flex: 1,
          }}
        >
          {brandYear}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.75,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', flex: 1, minWidth: 0 }}>
          {item.equipment_type && (
            <Chip label={item.equipment_type.name} size="small" sx={getTypeChipSx(theme, typeName)} />
          )}
          {item.equipment_subtype && (
            <Chip
              label={item.equipment_subtype.name}
              size="small"
              sx={
                isShoeType
                  ? getShoeSubtypeChipSx(theme, item.equipment_subtype.name ?? '')
                  : getSubtypeChipSx(theme, item.equipment_subtype.name ?? '')
              }
            />
          )}
        </Box>
      </Box>
      <Grid container spacing={0.75} sx={{ mb: 0.75 }}>
        {specCells.map(({ id, shortLabel, display, valueColor, isBlank }) => {
          return (
            <Grid item xs={4} key={id}>
              <Box
                sx={{
                  bgcolor: (t) => alpha(t.palette.grey[500], 0.05),
                  borderRadius: 1,
                  px: 1,
                  py: 0.75,
                  height: '100%',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 400,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                    lineHeight: 1.2,
                    display: 'block',
                  }}
                >
                  {isBlank ? '\u00A0' : shortLabel}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: valueColor,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    lineHeight: 1.25,
                    mt: 0.25,
                  }}
                >
                  {isBlank ? '\u00A0' : display}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      {/* MANAGERS row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 0.75,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              flexShrink: 0,
            }}
          >
            Managers
          </Typography>
          {visibleManagers.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
              None
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {visibleManagers.map((m) => (
                <Box key={m.id} title={`${m.firstname ?? ''} ${m.lastname ?? ''}`.trim()}>
                  <UserAvatar
                    src={m.profile_image}
                    firstname={m.firstname}
                    lastname={m.lastname}
                    userId={m.id}
                    size={22}
                    fontSize="0.55rem"
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {showDistanceUsed && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              fontWeight: 600,
              lineHeight: 1.35,
              flexShrink: 0,
              textAlign: 'right',
            }}
          >
            {distNum} {distanceUnit}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default EquipmentSearchCard;
